import {
  createContext,
  useCallback,
  useContext,
  useLayoutEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import type { Catalog, MessageKey } from "./catalog";
import { formatDate, type DateInput } from "./date";
import { DEFAULT_LOCALE, isLocale, type Direction, type Locale } from "./locale";
import {
  createTranslator,
  type MessageParams,
  type MissingMessageHandler,
  type Translator,
} from "./translate";

export interface I18nContextValue {
  readonly locale: Locale;
  readonly dir: Direction;
  readonly t: (key: MessageKey, params?: MessageParams) => string;
  readonly setLocale: (locale: Locale) => void;
}

function contextFrom(translator: Translator, setLocale: (locale: Locale) => void) {
  return { locale: translator.locale, dir: translator.dir, t: translator.t, setLocale };
}

// Components rendered outside a provider (unit tests, isolated previews) still get
// real Arabic strings instead of crashing or rendering keys.
const I18nContext = createContext<I18nContextValue>(
  contextFrom(createTranslator({ locale: DEFAULT_LOCALE }), () => {
    console.warn("[i18n] setLocale called outside <I18nProvider>");
  }),
);

export interface I18nProviderProps {
  /** Controlled locale. Omit to let the provider own the state. */
  readonly locale?: Locale;
  /**
   * Starting locale for the uncontrolled mode. WP-106 wiring point: pass the signed-in
   * user's `User.locale` here (falls back to the Arabic default).
   */
  readonly initialLocale?: Locale;
  /**
   * `localStorage` key that remembers the visitor's choice across reloads (uncontrolled mode only).
   * A stored locale wins over `initialLocale`; storage failures (private mode, blocked) are ignored.
   */
  readonly persistKey?: string;
  readonly onLocaleChange?: (locale: Locale) => void;
  /** Bind `<html lang dir>` to the locale (default `true`). Disable for nested previews. */
  readonly bindDocument?: boolean;
  /** Document title key, re-translated whenever the locale changes. */
  readonly titleKey?: MessageKey;
  readonly catalogs?: Partial<Record<Locale, Catalog>>;
  readonly onMissing?: MissingMessageHandler;
  readonly children: ReactNode;
}

function readStoredLocale(key: string | undefined): Locale | null {
  if (!key) return null;
  try {
    const stored = globalThis.localStorage?.getItem(key);
    return isLocale(stored) ? stored : null;
  } catch {
    return null;
  }
}

function writeStoredLocale(key: string | undefined, locale: Locale): void {
  if (!key) return;
  try {
    globalThis.localStorage?.setItem(key, locale);
  } catch {
    // storage unavailable: the choice just lasts for this page view
  }
}

export function I18nProvider(props: I18nProviderProps) {
  const {
    locale: controlled,
    initialLocale = DEFAULT_LOCALE,
    persistKey,
    onLocaleChange,
    bindDocument = true,
    titleKey,
    catalogs,
    onMissing,
    children,
  } = props;
  const [own, setOwn] = useState<Locale>(() => readStoredLocale(persistKey) ?? initialLocale);
  const locale = controlled ?? own;

  const setLocale = useCallback(
    (next: Locale) => {
      if (controlled === undefined) setOwn(next);
      writeStoredLocale(persistKey, next);
      onLocaleChange?.(next);
    },
    [controlled, onLocaleChange, persistKey],
  );

  const translator = useMemo(
    () =>
      createTranslator({
        locale,
        ...(catalogs ? { catalogs } : {}),
        ...(onMissing ? { onMissing } : {}),
      }),
    [locale, catalogs, onMissing],
  );

  // Layout effect: flip direction before paint so there is no LTR flash on switch.
  useLayoutEffect(() => {
    if (!bindDocument || typeof document === "undefined") return;
    const html = document.documentElement;
    html.lang = translator.locale;
    html.dir = translator.dir;
    if (titleKey) document.title = translator.t(titleKey);
  }, [bindDocument, translator, titleKey]);

  const value = useMemo(() => contextFrom(translator, setLocale), [translator, setLocale]);
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nContextValue {
  return useContext(I18nContext);
}

/** Formats a date with the given `Intl.DateTimeFormat` options; `—` for a missing/invalid value. */
export type FormatDate = (
  value: DateInput | null | undefined,
  options?: Intl.DateTimeFormatOptions,
) => string;

/**
 * Date formatter bound to the app locale (from `I18nProvider`), so dates follow the language the
 * user picked rather than the browser's locale. Use `DATE_TIME_FORMAT` from `@wato/i18n` for
 * date + time.
 */
export function useFormatDate(): FormatDate {
  const { locale } = useI18n();
  return useCallback<FormatDate>((value, options) => formatDate(locale, value, options), [locale]);
}
